import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, ShieldX, ListVideo, Sparkles, Clock } from "lucide-react";
import api from "@/lib/api";

function formatTimestamp(seconds) {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

/** Fully public page — the host shared this link, no login required to watch. */
export default function SharedRecordingPage() {
  const { token } = useParams();
  const videoRef = useRef(null);
  const [recording, setRecording] = useState(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/meetings/recordings/shared/${token}`)
      .then((res) => setRecording(res.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [token]);

  const seekTo = (seconds) => {
    if (!videoRef.current) return;
    videoRef.current.currentTime = seconds;
    videoRef.current.play().catch(() => {});
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center" data-testid="shared-recording-loading">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-400" />
      </div>
    );
  }

  if (error || !recording) {
    return (
      <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center gap-3 text-center px-6" data-testid="shared-recording-invalid">
        <ShieldX className="w-10 h-10 text-rose-400" />
        <p className="text-white font-semibold">This share link is invalid or has been revoked.</p>
        <p className="text-sm text-slate-400">Ask the host to share a fresh link.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 px-5 sm:px-8 py-8" data-testid="shared-recording-page">
      <div className="max-w-4xl mx-auto">
        <h1 className="font-heading text-2xl font-semibold text-white mb-1">{recording.title}</h1>
        <p className="text-xs text-slate-500 mb-5">Shared by the host · {new Date(recording.created_at).toLocaleDateString()}</p>

        <div className="w-full aspect-video rounded-2xl bg-black/40 border border-white/10 overflow-hidden mb-5" data-testid="shared-recording-video-player">
          <video ref={videoRef} src={`${api.defaults.baseURL}/meetings/recordings/shared/${token}/stream`} controls className="w-full h-full" data-testid="shared-recording-video-element" />
        </div>

        {recording.highlights?.length > 0 && (
          <div className="glass-panel rounded-2xl p-4 mb-5" data-testid="shared-recording-highlights-panel">
            <h4 className="text-xs font-bold uppercase tracking-wider text-cyan-300 mb-2 flex items-center gap-1.5">
              <ListVideo className="w-3.5 h-3.5" /> Highlights
            </h4>
            <div className="space-y-1">
              {recording.highlights.map((h, i) => (
                <button key={i} onClick={() => seekTo(h.timestamp)} data-testid={`shared-recording-highlight-${i}`}
                  className="w-full flex items-center gap-2.5 text-left text-sm text-slate-300 hover:text-white hover:bg-white/5 rounded-lg px-2 py-1.5 transition-colors">
                  <span className="text-xs font-mono text-cyan-400 shrink-0 bg-cyan-400/10 rounded px-1.5 py-0.5">{formatTimestamp(h.timestamp)}</span>
                  {h.label}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="glass-panel rounded-2xl p-5" data-testid="shared-recording-ai-notes">
          {!recording.ai_notes ? (
            <p className="text-sm text-slate-500 flex items-center gap-2"><Clock className="w-4 h-4" /> AI notes aren't ready yet — check back soon.</p>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center gap-1.5 text-amber-300"><Sparkles className="w-4 h-4" /> <h4 className="text-xs font-bold uppercase tracking-wider">Summary</h4></div>
              <p className="text-sm text-slate-300 leading-relaxed">{recording.ai_notes.summary}</p>
              <div>
                <h4 className="text-xs font-bold uppercase tracking-wider text-amber-300 mb-1.5">Key Points</h4>
                <ul className="space-y-1.5">
                  {recording.ai_notes.key_points.map((p, i) => (
                    <li key={i} className="text-sm text-slate-300 flex gap-2"><span className="text-amber-400">•</span> {p}</li>
                  ))}
                </ul>
              </div>
              <div>
                <h4 className="text-xs font-bold uppercase tracking-wider text-amber-300 mb-1.5">Topics Covered</h4>
                <div className="flex flex-wrap gap-1.5">
                  {recording.ai_notes.topics_covered.map((t, i) => (
                    <span key={i} className="text-xs font-medium bg-white/5 border border-white/10 text-slate-300 px-2.5 py-1 rounded-full">{t}</span>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
